import { useRef } from "react";
import PropertyImages from "../../../stories/property-detail/property-images";
import PriceLayout from "../../../stories/property-detail/price-layout";
import styles from "../property-details/property-details.module.scss";
import PropertyDetailHeader from "../../../stories/property-detail/property-detail-header";
import { objPriceLayout } from "./property";
import BidDetailsSkeleton from "../../../skeleton/properties-detail/BidDetails";
import AgentInfoSkeleton from "../../../skeleton/properties-detail/AgentInfo";
import PlaceOfferSkeleton from "../../../skeleton/properties-detail/PlaceOffer";
import SkeletonC from "../../../stories/common/skeleton";
import { usePropertyDetail } from "../../../hooks/react-query/properties/usePropertyDetail";
import PropertyDesc from "../../../stories/property-detail/property-description";
import AgentInfo from "../../../stories/property-detail/agent-info";
import { numberWithCommas, phoneMask } from "../../../utils/const";
import BidDetails from "../../../stories/property-detail/bid-details";
import PlaceOffer from "../../../stories/property-detail/place-offer";
import PropertyFeatures from "../../../stories/property-detail/property-features";
import MobileDetailsScreen from "../../../stories/common/mobile-property-details";
import RelatedProperties from "../../../stories/property-detail/related-properties";
import PropertyDocuments from "../../../stories/property-detail/property-documents";
import CopyLink from "./copy-link";
import { useCreateShareLink } from "../../../hooks/react-query/properties/useCreateShareLink";
import { useSalesInstruction } from "../../../hooks/react-query/properties/useSalesInstruction";
import {
  cleanPhoneNumber,
  countDaysFromDate,
  StatusObjectColorFilter
} from "../../../utils/utils";
import Like from "../../../stories/common/like";
import { Tag } from "primereact/tag";

const PropertyDetails = ({ propertyId }: { propertyId: string }) => {
  const op: any = useRef(null);

  const { data: objPropertyDetails, isLoading: loading } =
    usePropertyDetail(propertyId);

  const { data: internalShareLink, isLoading: isLoadingInternal } =
    useCreateShareLink("internal", propertyId);

  const { data: externalShareLink, isLoading } = useCreateShareLink(
    "external",
    propertyId
  );

  const { data: salesInstruction } = useSalesInstruction(
    objPropertyDetails?.sfid
  );

  return (
    <>
      <MobileDetailsScreen
        type={"internal"}
        objPropertyDetails={objPropertyDetails}
        status={StatusObjectColorFilter(objPropertyDetails?.status)}
        isLoading={loading}
      />
      <CopyLink
        internalShareLink={internalShareLink}
        externalShareLink={externalShareLink}
        op={op}
      />
      <div className={styles.propertyDetails}>
        <div className={`${styles.content}`}>
          <>
            {loading ? (
              <div className={styles.propertyHeader}>
                <SkeletonC
                  width="28%"
                  height="24px"
                  className={styles.skeleton}
                  shape="rectangle"
                />
                <SkeletonC
                  width="24px"
                  height="24px"
                  shape="circle"
                  className={styles.skeleton}
                />
              </div>
            ) : (
              <div className={styles.propertyHeader}>
                <PropertyDetailHeader
                  address={`${objPropertyDetails?.address ?? ""}`}
                />
                <div className={styles.shareDetails}>
                  <div
                    className={styles.share}
                    onClick={(e) => op?.current?.toggle(e)}
                  >
                    {!isLoadingInternal && !isLoading && (
                      <>
                        {process.env.REACT_APP_ENABLE_SHARE === "true" && (
                          <img src="/images/share.svg" width={23} alt="" />
                        )}
                      </>
                    )}
                  </div>
                  {objPropertyDetails?.id && (
                    <Like
                      loginType={"agent"}
                      propertyId={objPropertyDetails.id}
                      favCount={objPropertyDetails?.favCount}
                      objPropertyDetails={objPropertyDetails}
                      width={24}
                    />
                  )}
                </div>
              </div>
            )}
          </>
          <div className={styles.propertyContentWrap}>
            {loading ? (
              <SkeletonC
                width="100%"
                height="535px"
                borderRadius="17.95px"
                className={styles.skeleton}
              />
            ) : (
              <>
                <div className={styles.statusWrap}>
                  {objPropertyDetails?.status && (
                    <div
                      className={styles.status}
                      style={{
                        backgroundColor:
                          StatusObjectColorFilter(objPropertyDetails?.status)
                            ?.bgColor || "#0000008a",
                        color:
                          StatusObjectColorFilter(objPropertyDetails?.status)
                            ?.txtColor || "#ffffff"
                      }}
                    >
                      {objPropertyDetails?.status}
                    </div>
                  )}
                  <Tag
                    value={
                      countDaysFromDate(objPropertyDetails?.createdAt) > 1
                        ? `${countDaysFromDate(
                            objPropertyDetails?.createdAt
                          )} Days ago`
                        : `${countDaysFromDate(
                            objPropertyDetails?.createdAt
                          )} Day ago`
                    }
                    style={{
                      background: "#0000008a",
                      color: "#ffffff"
                    }}
                    className={styles.dayCount}
                  />
                </div>
                <PropertyImages
                  imagesArr={objPropertyDetails?.images?.filter(
                    (value: any) => value?.url
                  )}
                />
              </>
            )}
            <div className={styles.propertyWrap}>
              <div className={styles.propertyDescription}>
                {loading ? (
                  <>
                    <SkeletonC
                      width="25%"
                      height="50px"
                      className={styles.skeleton}
                    />
                    <SkeletonC
                      width="35%"
                      height="30px"
                      className={styles.skeleton}
                    />
                    <SkeletonC
                      width="40%"
                      height="30px"
                      className={styles.skeleton}
                    />
                  </>
                ) : (
                  <PriceLayout
                    type={"agent"}
                    priceDrop={objPropertyDetails?.priceDrop}
                    priceDropDate={objPropertyDetails?.priceDropDate}
                    amount={objPropertyDetails?.askingPrice}
                    currencyCode="USD"
                    locale="en-US"
                    house={objPropertyDetails?.objPriceLayout?.house}
                    sqft={
                      +objPropertyDetails?.sqft > 0
                        ? numberWithCommas(objPropertyDetails?.sqft)
                        : null
                    }
                    lotSizeSqFt={
                      +objPropertyDetails?.lotSizeSqFt > 0
                        ? numberWithCommas(objPropertyDetails?.lotSizeSqFt)
                        : null
                    }
                    bed={
                      objPropertyDetails?.beds || objPropertyDetails?.bedRooms
                    }
                    bath={
                      objPropertyDetails?.baths || objPropertyDetails?.totalBath
                    }
                    garage={objPropertyDetails?.garage}
                    sqftText="Sq.Ft"
                    address={objPropertyDetails?.address}
                  />
                )}

                {loading ? (
                  <SkeletonC
                    width="100%"
                    height="7rem"
                    className="mb-2 "
                    shape="rectangle"
                  />
                ) : (
                  <PropertyDesc
                    title={"Property Description"}
                    propertyDetails={objPropertyDetails?.description}
                    viewMoreWords={250}
                  />
                )}
                {loading ? (
                  <SkeletonC
                    width="100%"
                    height="7rem"
                    className="mb-2 "
                    shape="rectangle"
                  />
                ) : (
                  <>
                    {objPropertyDetails?.addlVerbiageForBS && (
                      <PropertyDesc
                        Notes={false}
                        title={"Additional Verbiage for BS"}
                        propertyDetails={objPropertyDetails?.addlVerbiageForBS}
                        viewMoreWords={500}
                      />
                    )}
                  </>
                )}
                {loading ? (
                  <SkeletonC
                    width="100%"
                    height="7rem"
                    className="mb-2 "
                    shape="rectangle"
                  />
                ) : (
                  <>
                    {salesInstruction?.Instructions__c && (
                      <PropertyDesc
                        title={"Additional Information"}
                        propertyDetails={salesInstruction?.Instructions__c}
                        viewMoreWords={250}
                      />
                    )}
                  </>
                )}
                {loading ? (
                  <SkeletonC
                    width="100%"
                    height="7rem"
                    className="mb-2 "
                    shape="rectangle"
                  />
                ) : (
                  <>
                    {objPropertyDetails?.internalNotes && (
                      <PropertyDesc
                        Notes={true}
                        title={"Internal Notes"}
                        propertyDetails={objPropertyDetails?.internalNotes}
                        viewMoreWords={500}
                      />
                    )}
                  </>
                )}
                {objPropertyDetails?.docs?.length > 0 && (
                  <PropertyDocuments
                    title={"Documents"}
                    objPropertyDetails={objPropertyDetails}
                    loading={loading}
                  />
                )}

                {loading ? (
                  <BidDetailsSkeleton />
                ) : (
                  <BidDetails
                    title={"Bid Details"}
                    dateTimeTitle={"Showing Date and Time"}
                    dateTime={objPropertyDetails?.showingDateAndTime}
                    company={"BS Escrow Company"}
                    company_subtitle={objPropertyDetails?.bsEscrowCompany}
                    offersDueTitle={"Offer Due Date and Time"}
                    offersDateTime={objPropertyDetails?.offerDueDateAndTime}
                    closeTitle={"Close Date"}
                    closeDate={objPropertyDetails?.closeDate}
                    depositTitle={"BS Deposit"}
                    amount={objPropertyDetails?.bsDeposit}
                    currencyCode="USD"
                    locale="en-US"
                    sales={"Sales Instructions"}
                    salesInstruction={objPropertyDetails?.salesInstruction}
                  />
                )}
                {(objPropertyDetails?.bedRooms ||
                  objPropertyDetails?.beds ||
                  objPropertyDetails?.baths ||
                  objPropertyDetails?.totalBath ||
                  objPropertyDetails?.lotSizeSqFt ||
                  objPropertyDetails?.yearBuilt) && (
                  <PropertyFeatures
                    title={"Property Features"}
                    objPropertyDetails={objPropertyDetails}
                    minShowData={4}
                    loading={loading}
                  />
                )}
              </div>
              <div className={styles.agentBox}>
                {loading ? (
                  <AgentInfoSkeleton />
                ) : (
                  <>
                    {objPropertyDetails?.acquisitionAgent1?.name && (
                      <AgentInfo
                        // profileImg={
                        //   objPropertyDetails?.acquisitionAgent1?.mediumPhotoUrl
                        // }
                        title={"Acquisition Agent"}
                        name={`${
                          objPropertyDetails?.acquisitionAgent1?.name?.split(
                            " "
                          )[0] ?? ""
                        } ${
                          objPropertyDetails?.acquisitionAgent1?.name?.split(
                            " "
                          )[1] ?? ""
                        }`}
                        email={objPropertyDetails?.acquisitionAgent1Email ?? ""}
                        phone={
                          phoneMask(
                            Number(
                              cleanPhoneNumber(
                                objPropertyDetails?.acquisitionAgent1Phone
                              )
                            )
                          ) ?? null
                        }
                      />
                    )}
                  </>
                )}
                <div className={styles.placeOffer}>
                  {loading ? (
                    <PlaceOfferSkeleton />
                  ) : (
                    <>
                      {/* {(objPropertyDetails?.offerDueDateAndTime === null ||
                        moment(objPropertyDetails?.offerDueDateAndTime).isAfter(
                          moment().utc()
                        )) && ( */}
                      <PlaceOffer
                        sifd={objPropertyDetails?.sfid}
                        disabled={objPropertyDetails?.status === "Sold"}
                        price={objPriceLayout.amount}
                        address={objPriceLayout.address}
                        propertyId={propertyId}
                        bidCount={objPropertyDetails?.bidCount}
                        // viewProperty={state?.state?.propertyDetails}
                      />
                      {/* )} */}
                    </>
                  )}
                </div>
              </div>
            </div>
            <RelatedProperties
              _propertyId={propertyId}
              title={"Related Properties"}
              // relatedPropertiesArray={relatedPropertiesArray}
              currencyCode={"USD"}
              locale={"en-US"}
              loading={loading}
            />
          </div>
        </div>
      </div>
      {/* <span className={styles.version}>{pack.version}</span> */}
    </>
  );
};

export default PropertyDetails;
